import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
    CalendarBlankIcon,
    ExportIcon,
    FunnelSimpleIcon,
    ListBulletsIcon,
    UserIcon,
} from '@phosphor-icons/react';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import { Table } from '@/components/Table';
import {
    ActionBar,
    ActionBarInteractionTrigger,
    ActionType,
    ActiveIndicator,
    type ActionConfig,
    type MenuItemConfig,
} from '@/components/ActionBar';
import { DateTimeRangePicker } from '@/components';
import type { TableColumn } from '@/types/table';
import type { AuditEvent } from '@/types/admin-settings';
import { AdminSettingsService } from '@/services/engine/admin-settings-service';
import { toast } from '@/components';
import { useAuditEvents } from '../hooks/useAuditEvents';
import { useAuditEventColumns } from '../hooks/useAuditEventColumns';
import { MemberCell } from './MemberCell';
import { LogsEventTypesFilter } from './LogsEventTypesFilter';
import { LogsUserFilter } from './LogsUserFilter';
import { ExpandableDetailsCell } from '@/components';

function formatDetails(event: AuditEvent): string {
    const hasPayload = event.details && Object.keys(event.details).length > 0;
    return hasPayload ? JSON.stringify(event.details) : '';
}

const PAGE_SIZE = 20;

export function LogsTab() {
    const { t } = useTranslation(I18nNamespace.SETTINGS);
    const [searchParams, setSearchParams] = useSearchParams();
    const [isExporting, setIsExporting] = useState(false);

    const page = Math.max(
        1,
        Number.parseInt(searchParams.get('page') ?? '1', 10) || 1,
    );
    const selectedEventTypes = searchParams.getAll('eventType');
    const startParam = searchParams.get('from');
    const endParam = searchParams.get('to');
    const startDate = startParam ? new Date(startParam) : undefined;
    const endDate = endParam ? new Date(endParam) : undefined;
    const selectedUserIds = searchParams.getAll('user');

    const updateParams = (
        mutate: (params: URLSearchParams) => void,
        { keepPage = false }: { keepPage?: boolean } = {},
    ) => {
        setSearchParams(
            (prev) => {
                const params = new URLSearchParams(prev);
                mutate(params);
                if (!keepPage) params.delete('page');
                return params;
            },
            { replace: true },
        );
    };

    const setPage = (next: number) =>
        updateParams(
            (params) => {
                if (next <= 1) params.delete('page');
                else params.set('page', String(next));
            },
            { keepPage: true },
        );
    const setSelectedEventTypes = (next: string[]) =>
        updateParams((params) => {
            params.delete('eventType');
            next.forEach((type) => params.append('eventType', type));
        });
    const setDateRange = (from: Date | undefined, to: Date | undefined) =>
        updateParams((params) => {
            if (from) params.set('from', from.toISOString());
            else params.delete('from');
            if (to) params.set('to', to.toISOString());
            else params.delete('to');
        });
    const setSelectedUserIds = (next: string[]) =>
        updateParams((params) => {
            params.delete('user');
            next.forEach((id) => params.append('user', id));
        });

    const { events, total, isLoading } = useAuditEvents({
        page,
        size: PAGE_SIZE,
        eventTypes:
            selectedEventTypes.length > 0 ? selectedEventTypes : undefined,
        startDate: startDate?.toISOString(),
        endDate: endDate?.toISOString(),
        userIds: selectedUserIds.length > 0 ? selectedUserIds : undefined,
    });

    const sharedColumns = useAuditEventColumns();
    const columns: TableColumn<AuditEvent>[] = [
        ...sharedColumns,
        {
            header: t('logs.initiatedBy'),
            cell: ({ row }) => {
                const {
                    userId,
                    userEmail: eventUserEmail,
                    userName,
                } = row.original;
                if (userId && userName) {
                    return (
                        <MemberCell userId={userId} displayName={userName} />
                    );
                }
                const display = userName ?? eventUserEmail ?? userId ?? '-';
                return <span className="text-sm text-grey-300">{display}</span>;
            },
            width: '220px',
        },
        {
            header: t('logs.details'),
            cell: ({ row, isExpanded, toggleExpand }) => {
                const text = formatDetails(row.original);
                if (!text) return null;
                return (
                    <ExpandableDetailsCell
                        text={text}
                        isExpanded={!!isExpanded}
                        onToggle={() => toggleExpand?.()}
                        expandLabel={t('logs.expandDetails')}
                        collapseLabel={t('logs.collapseDetails')}
                    />
                );
            },
        },
    ];

    const handleExport = async () => {
        setIsExporting(true);
        try {
            await AdminSettingsService.exportAuditEvents({
                eventTypes:
                    selectedEventTypes.length > 0
                        ? selectedEventTypes
                        : undefined,
                startDate: startDate?.toISOString(),
                endDate: endDate?.toISOString(),
                userIds:
                    selectedUserIds.length > 0 ? selectedUserIds : undefined,
            });
            toast.success(t('logs.exportSuccess'));
        } catch {
            toast.error(t('logs.exportFailed'));
        } finally {
            setIsExporting(false);
        }
    };

    const hasAnyFilter =
        selectedEventTypes.length > 0 ||
        !!startDate ||
        !!endDate ||
        selectedUserIds.length > 0;

    const filterItems: MenuItemConfig[] = [
        {
            icon: ListBulletsIcon,
            label: t('logs.eventTypesLabel'),
            isActive: selectedEventTypes.length > 0,
            activeIndicator: ActiveIndicator.DOT,
            children: (
                <LogsEventTypesFilter
                    selected={selectedEventTypes}
                    onChange={setSelectedEventTypes}
                    onClear={() => setSelectedEventTypes([])}
                />
            ),
        },
        {
            icon: CalendarBlankIcon,
            label: t('logs.dateRange'),
            isActive: !!startDate || !!endDate,
            activeIndicator: ActiveIndicator.DOT,
            children: (
                <DateTimeRangePicker
                    initialStartDate={startDate}
                    initialEndDate={endDate}
                    onApply={(range) =>
                        setDateRange(range.startTime, range.endTime)
                    }
                    onClear={() => setDateRange(undefined, undefined)}
                />
            ),
        },
        {
            icon: UserIcon,
            label: t('logs.user'),
            isActive: selectedUserIds.length > 0,
            activeIndicator: ActiveIndicator.DOT,
            children: (
                <LogsUserFilter
                    selected={selectedUserIds}
                    onChange={setSelectedUserIds}
                    onClear={() => setSelectedUserIds([])}
                />
            ),
        },
    ];

    const actions: ActionConfig[] = [
        {
            type: ActionType.MENU,
            icon: FunnelSimpleIcon,
            label: t('logs.filters'),
            trigger: ActionBarInteractionTrigger.CLICK,
            title: t('logs.filters'),
            active: hasAnyFilter,
            activeIndicator: ActiveIndicator.DOT,
            onClear: hasAnyFilter
                ? () =>
                      updateParams((params) => {
                          params.delete('eventType');
                          params.delete('from');
                          params.delete('to');
                          params.delete('user');
                      })
                : undefined,
            items: filterItems,
        },
        {
            type: ActionType.ITEM,
            icon: ExportIcon,
            label: t('logs.export'),
            onClick: handleExport,
            disabled: isExporting,
        },
    ];

    return (
        <div className="flex flex-col gap-4 pb-24">
            <div className="flex justify-center">
                <ActionBar actions={actions} />
            </div>

            <Table<AuditEvent>
                data={events}
                columns={columns}
                isLoading={isLoading}
                page={page}
                pageSize={PAGE_SIZE}
                totalCount={total}
                onPageChange={setPage}
                resizableColumns
                expandableRow={(event) => (
                    <pre className="text-xs text-grey-200 font-geist-mono whitespace-pre-wrap break-words bg-grey-700 rounded-[6px] p-3 border border-grey-600">
                        {JSON.stringify(event.details, null, 2)}
                    </pre>
                )}
                emptyState={{
                    title: t('logs.empty'),
                }}
            />
        </div>
    );
}
